import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import voteDataService from '../utils/services/voteDataService';

// Vote data is served by the authenticated backend API (see bg_api.md) rather
// than static JSON bundled with the app, so it is never exposed to
// unauthenticated visitors. The payload is fetched once per session after
// login and held in memory; the combined shape exposed to consumers is
// unchanged from the static-file era.

const VoteDataContext = createContext(null);

export const useVoteData = () => {
  const ctx = useContext(VoteDataContext);
  if (!ctx) throw new Error('useVoteData must be used within VoteDataProvider');
  return ctx;
};

// Re-key the API payload into the combined object components already expect.
const toCombinedData = (payload) => {
  const gv = payload.game_vote_data;
  return {
    leaderboard: payload.leaderboard,
    game_vote_data: gv.main,
    game_vote_data_byb: gv.byb,
    game_vote_data_espn: gv.espn,
    game_vote_data_betfair: gv.betfair,
    game_vote_data_count_1: gv.count1,
    game_vote_data_count_2: gv.count2,
    game_vote_data_count_3: gv.count3,
    game_vote_data_count_4: gv.count4,
    game_vote_data_count_5: gv.count5,
    datasets: {
      main: gv.main,
      byb: gv.byb,
      espn: gv.espn,
      betfair: gv.betfair,
      count1: gv.count1,
      count2: gv.count2,
      count3: gv.count3,
      count4: gv.count4,
      count5: gv.count5,
    },
  };
};

export const VoteDataProvider = ({ children }) => {
  const { isAuthenticated, logout } = useAuth();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Several pages call fetchVoteData on mount; share one in-flight request so
  // the payload is only ever fetched once (also keeps us under the 10/min
  // rate limit).
  const inFlightRef = useRef(null);
  const dataRef = useRef(null);
  dataRef.current = data;

  const fetchVoteData = useCallback(async () => {
    if (dataRef.current) return dataRef.current;
    if (inFlightRef.current) return inFlightRef.current;

    const request = (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await voteDataService.fetchVoteData();

        if (result.success) {
          const combined = toCombinedData(result.data);
          setData(combined);
          return combined;
        }

        if (result.isAuthError) {
          // Token rejected by the backend — route through the standard
          // session-expired flow instead of surfacing an inline error.
          sessionStorage.setItem('sessionExpired', 'true');
          logout();
          return null;
        }

        // 403 (no role) is expected for locked accounts — the coming-soon
        // overlay is already covering the app, so keep the message quiet
        // unless something else went wrong.
        if (!result.isRoleError) {
          setError(result.message);
        }
        return null;
      } finally {
        setIsLoading(false);
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = request;
    return request;
  }, [logout]);

  // Hydrate once per session as soon as the user is authenticated, and drop
  // the payload from memory on logout so it never outlives the session.
  useEffect(() => {
    if (isAuthenticated) {
      fetchVoteData();
    } else {
      setData(null);
      setError(null);
    }
  }, [isAuthenticated, fetchVoteData]);

  const clear = useCallback(() => {
    setError(null);
  }, []);

  return (
    <VoteDataContext.Provider value={{ data, isLoading, error, fetchVoteData, clear }}>
      {children}
    </VoteDataContext.Provider>
  );
};
